import { Component, Input, OnInit } from '@angular/core';
import { EMPTY, Subscription, forkJoin } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { expand, finalize, pairwise } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';

import { ChartDataset, ChartLabel } from 'src/app/core/entities/chart-dataset';
import { AutoUnsub } from 'src/app/core/decorators/auto-unsub.decorator';
import { ChartDataPoint } from 'src/app/core/entities/chart-data-point.entity';
import { ChartDescription } from 'src/app/core/entities/chart-description';
import { ChartEvaluation } from 'src/app/core/entities/chart-evaluation';
import { ChartService } from 'src/app/core/services/chart.service';
import { EvaluationService } from 'src/app/core/services/evaluation.service';
import { Feature } from 'src/app/core/entities/feature.entity';
import { FeatureService } from 'src/app/core/services/feature.service';
import { Patient } from 'src/app/core/entities/patient.entity';

@Component({
  selector: 'app-vision',
  templateUrl: './vision.component.html',
  styleUrls: ['./vision.component.css'],
})
@AutoUnsub()
export class VisionComponent implements OnInit {
  @Input() public patientProfile: Patient = new Patient();
  private _observers: Subscription = new Subscription();
  private _features: Feature[] = [];
  public evaluation: ChartEvaluation = {
    evaluationCode: EvaluationService.EVALUATION_VISION,
    features: [
      {
        featureId: FeatureService.FEATURE_VISION_MONOL_MONOYER,
        label: $localize`:@@patient_evaluationVision_featureVisionMonoLMonoyerLabel:Left eye acuity`,
        min: 0,
        max: 10,
        chartDatasetOption: {
          type: 'line',
          color: '#2e5bff',
          useTickModel: true,
          tooltip: {
            label: $localize`:@@patient_evaluationVision_featureVisionMonoLMonoyerTooltipLabel:Left eye / Monoyer acuity`,
            unit: '',
            pattern: /(.*)/,
            replace: '$1/10',
          },
          nbDecimalDigits: 0,
          nbSteps: 10,
          pointStyle: 'circle',
          axis: {
            id: 'y',
            label: $localize`:@@patient_evaluationVision_featureVisionMonoLMonoyerYTooltipLabel:Monoyer acuity`,
            type: 'linear',
            position: 'left',
            beginAtZero: true,
          },
        },
      },
      {
        featureId: FeatureService.FEATURE_VISION_MONOR_MONOYER,
        label: $localize`:@@patient_evaluationVision_featureVisionMonoRMonoyerLabel:Right eye acuity`,
        min: 0,
        max: 10,
        chartDatasetOption: {
          type: 'line',
          color: '#6225e1',
          useTickModel: true,
          tooltip: {
            label: $localize`:@@patient_evaluationVision_featureVisionMonoRMonoyerTooltipLabel:Right eye / Monoyer acuity`,
            unit: '',
            pattern: /(.*)/,
            replace: '$1/10',
          },
          nbDecimalDigits: 0,
          nbSteps: 10,
          pointStyle: 'circle',
        },
      },
      {
        featureId: FeatureService.FEATURE_VISION_BIN_MONOYER,
        label: $localize`:@@patient_evaluationVision_featureVisionBinMonoyerLabel:Binocular acuity`,
        min: 0,
        max: 10,
        chartDatasetOption: {
          type: 'line',
          color: '#0dc49c',
          useTickModel: true,
          tooltip: {
            label: $localize`:@@patient_evaluationVision_featureVisionBinMonoyerTooltipLabel:Binocular / Monoyer acuity`,
            unit: '',
            pattern: /(.*)/,
            replace: '$1/10',
          },
          nbDecimalDigits: 0,
          nbSteps: 10,
          pointStyle: 'circle',
        },
      },
    ],
    horizontalLimitLines: [
      {
        label: $localize`:@@patient_evaluationVision_horizontalLimitLineYLabel:Maximum acuity`,
        color: '#3d3d40',
        assignToAxisIds: {
          y: 10,
        },
      },
    ],
  };
  public monoyerDatasets: ChartDataset[] = [];
  public xLabels: ChartLabel[] = [];
  public chartDescription: ChartDescription;
  public dateRangeForm: FormGroup;
  public loading = true;
  public loadingChart = true;

  constructor(
    private featureService: FeatureService,
    private chartService: ChartService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.prepareDateRangeForm();

    this.resetChartDescription();
  }

  prepareDateRangeForm(): void {
    const startDate = moment.utc().subtract(6, 'months').format('MM/YYYY');
    const endDate = moment.utc().format('MM/YYYY');

    this.dateRangeForm = this.formBuilder.group({
      startDate: [
        startDate,
        [
          Validators.required,
          Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
        ],
      ],
      endDate: [
        endDate,
        [
          Validators.required,
          Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
        ],
      ],
    });

    this._observers.add(
      this.dateRangeForm.valueChanges.pipe(pairwise()).subscribe({
        next: ([previousRes, res]) => this.submitDateRangeForm(),
      })
    );

    this.loading = false;
  }

  changeDateRange(
    event: { monthIndex: number; year: number },
    key: string
  ): void {
    const value = moment
      .utc([event.year, event.monthIndex, 1])
      .format('MM/YYYY');

    this.dateRangeForm.get(key).setValue(value);
  }

  getDateYear(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return splitDate[1];
  }

  getDateMonth(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return parseInt(splitDate[0], 10) - 1;
  }

  submitDateRangeForm(): void {
    if (this.dateRangeForm.valid) {
      this.loadingChart = true;
      this.setChart();
    }
  }

  resetChartDescription(): void {
    this.chartDescription = {
      evaluationCode: this.evaluation.evaluationCode,
      evaluationLabel: $localize`:@@patient_evaluationVision_chartDescriptionLabel:of acuity`,
      measuringPointsTotal: 0,
      featuresGroups: [
        {
          label: '',
          features: [],
        },
      ],
    };
  }

  setChart(): void {
    this._features = [];
    this.monoyerDatasets = [];
    this.xLabels = [];

    // Reset chart description
    this.resetChartDescription();

    const startDate = moment.utc(
      this.dateRangeForm.get('startDate').value,
      'MM/YYYY'
    );
    const endDate = moment.utc(
      this.dateRangeForm.get('endDate').value,
      'MM/YYYY'
    );

    let params = {
      page: 1,
      startDate: startDate.toISOString(),
      endDate: endDate.endOf('month').toISOString(),
      patientUuid: this.route.snapshot.params['uuid'],
      featureIds: this.evaluation.features.map((item) => item.featureId),
    };

    const source$ = this.featureService.getFeatures(params).pipe(
      expand((res) => {
        params.page++;
        this._features = this._features.concat(res);
        return res.length < 50
          ? EMPTY
          : this.featureService.getFeatures(params);
      })
    );

    forkJoin([source$])
      .pipe(
        finalize(() => {
          this.loadingChart = false;
        })
      )
      .subscribe((res) => {
        let chartDataPoints = {};

        this.evaluation.features.forEach((feature) => {
          let groupedFeatures = this.featureService.groupFeatures(
            this._features,
            feature.featureId
          );
          chartDataPoints[feature.featureId] =
            this.chartService.setChartDataPoints(groupedFeatures);
        });

        if (Object.values(chartDataPoints).length > 0) {
          // Set labels
          const maxDataPoints = Object.values(chartDataPoints).reduce(
            (r: any, s: any) => (r.concat(s)),
            []
          );
          this.xLabels = this.chartService.setXLabels(
            maxDataPoints as ChartDataPoint[]
          );

          // Set datasets
          this.evaluation.features.forEach((feature) => {
            const dataset = this.chartService.setDataset(
              feature,
              chartDataPoints[feature.featureId],
              feature.chartDatasetOption,
              [],
              this.xLabels,
              feature.min ??
                (chartDataPoints[feature.featureId].length
                  ? chartDataPoints[feature.featureId][0].min
                  : null),
              feature.max ??
                (chartDataPoints[feature.featureId].length
                  ? chartDataPoints[feature.featureId][0].max
                  : null),
              feature.isMaxLimited
            );
            this.monoyerDatasets.push(dataset);
            this.chartDescription.featuresGroups[0].features.push({
              featureId: feature.featureId,
              hidden: feature.chartDatasetOption.hidden,
              data: dataset.data,
              unit: feature.chartDatasetOption.tooltip
                ? feature.chartDatasetOption.tooltip.unit
                : '',
              label: Array.isArray(feature.label)
                ? feature.label[0]
                : feature.label,
            });
          });
          this.chartDescription.featuresGroups[0].features.forEach(
            (dataset, i) => {
              if (
                this.chartDescription.featuresGroups[0].features[i].data.filter(
                  (item) => item.value !== null
                ).length > this.chartDescription.measuringPointsTotal
              ) {
                this.chartDescription.measuringPointsTotal =
                  this.chartDescription.featuresGroups[0].features[
                    i
                  ].data.filter((item) => item.value !== null).length;
              }
            }
          );
        }
      });
  }

  updateChartDescriptionFeature(chartDataset: ChartDataset): void {
    let feature = this.chartDescription.featuresGroups[0].features.find(
      (item) => item.featureId == chartDataset.id
    );
    feature.hidden = chartDataset.hidden;
  }
}
